import type { AssessmentMedia } from '../../model/assessments/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'

interface AssessmentTemplateMediaLinkApiOptions {
  templateId: string | number
  /** The object path stored in the document (`assessment_templates/{id}/{uuid}.{ext}`). */
  path: string
  onError?: (message: string) => void
}

/**
 * `GET /admin-api/assessment-templates/{template_id}/media?path=…` — re-sign (contract §7).
 *
 * Silent by design — it runs whenever an image comes into view, so a reference to a deleted object
 * would otherwise fire a toast on every render. The server answers `404` both for a missing object
 * and for a foreign path; either way the caller gets `null` and renders an inline note. `onError`
 * is there for a caller that wants the message, and `commonStore` is untouched for the same reason.
 */
export const useAssessmentTemplateMediaLinkApi = async (
  options: AssessmentTemplateMediaLinkApiOptions,
): Promise<AssessmentMedia | null> => {
  const { $axios } = useNuxtApp()

  const url = useAdminRoutes('admin.assessment-templates.media.show', {
    template_id: options.templateId,
    path: options.path,
  })

  if (!url) {
    options.onError?.('Unexpected error. Invalid request URL.')
    return null
  }

  try {
    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      options.onError?.(data.message)
      return null
    }

    const media: AssessmentMedia | null = data.data?.media ?? null

    if (!media?.url) {
      options.onError?.('The file could not be loaded.')
      return null
    }

    return media
  } catch (err: unknown) {
    options.onError?.(getAxiosErrorMessage(err))
    return null
  }
}
